import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Alert,
  Divider,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
  Badge,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  ListItemIcon,
  Pagination,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ShoppingCart,
  LocalShipping,
  CheckCircle,
  Pending,
  Cancel,
  Add,
  Visibility,
  Edit,
  Delete,
  Phone,
  LocationOn,
  Schedule,
  TrendingUp,
  TrendingDown,
  Assignment,
  Inventory2,
  Person,
  AttachMoney,
  NotificationImportant,
  FilterList,
  Search,
  Refresh,
  MoreVert,
  Receipt,
  Download,
  Remove,
} from '@mui/icons-material';
import { Pedido, EstadoPedido, getPedidos, cambiarEstadoPedido, getPedidoById, createPedido, CrearPedidoData } from '../../../services/pedidos';
import { productoService } from '../../../services/productos';
import { Producto } from '../../../types';
import { getClientes, Cliente, getDireccionesAuthCliente, DireccionAuth } from '../../../services/clientes';

// Styled Components con diseño El Piñón
const DashboardContainer = styled(Box)({
  background: 'linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%)',
  minHeight: '100vh',
  padding: '24px',
  '@media (max-width: 960px)': {
    padding: '20px 16px',
  },
  '@media (max-width: 600px)': {
    padding: '16px 8px',
  },
});

const HeaderSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '32px',
  padding: '24px',
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  borderRadius: '20px',
  color: 'white',
  boxShadow: '0 8px 32px rgba(46, 90, 51, 0.3)',
  flexWrap: 'wrap',
  gap: '16px',
  '@media (max-width: 960px)': {
    padding: '20px',
    marginBottom: '24px',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  '@media (max-width: 600px)': {
    padding: '16px',
    borderRadius: '12px',
  },
});

const StatsCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '16px',
  border: '2px solid #e8f5e8',
  boxShadow: '0 4px 20px rgba(46, 90, 51, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(46, 90, 51, 0.2)',
    borderColor: '#2e5a33',
  },
  '@media (max-width: 600px)': {
    '&:hover': {
      transform: 'none',
    },
  },
});

const OrderCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '16px',
  border: '1px solid #e8f5e8',
  marginBottom: '16px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 25px rgba(46, 90, 51, 0.15)',
    borderColor: '#2e5a33',
  },
  '@media (max-width: 600px)': {
    '&:hover': {
      transform: 'none',
    },
  },
});

const ActionButton = styled(Button)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  color: 'white',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  boxShadow: '0 4px 15px rgba(46, 90, 51, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #3d7342 0%, #2e5a33 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(46, 90, 51, 0.4)',
  },
  '@media (max-width: 600px)': {
    padding: '10px 16px',
    fontSize: '0.875rem',
  },
});

const FilterCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '16px',
  border: '1px solid #e8f5e8',
  marginBottom: '24px',
});

const DashboardPedidosOnline: React.FC = () => {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<string>('todos');
  const [busqueda, setBusqueda] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [pedidoSeleccionado, setPedidoSeleccionado] = useState<Pedido | null>(null);
  const [dialogDetalles, setDialogDetalles] = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState<EstadoPedido>('pendiente');
  const [actualizandoEstado, setActualizandoEstado] = useState(false);
  const [pedidoEditando, setPedidoEditando] = useState<number | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogConfirmacion, setDialogConfirmacion] = useState(false);
  const [generandoBoleta, setGenerandoBoleta] = useState(false);
  
  // Estados para crear pedido
  const [dialogCrearPedido, setDialogCrearPedido] = useState(false);
  const [creandoPedido, setCreandoPedido] = useState(false);
  const [productosDisponibles, setProductosDisponibles] = useState<Producto[]>([]);
  const [clientesDisponibles, setClientesDisponibles] = useState<Cliente[]>([]);
  const [direccionesCliente, setDireccionesCliente] = useState<DireccionAuth[]>([]);
  
  // Form states para nuevo pedido
  const [clienteSeleccionado, setClienteSeleccionado] = useState<number | null>(null);
  const [direccionSeleccionada, setDireccionSeleccionada] = useState<number | null>(null);
  const [metodoPagoSeleccionado, setMetodoPagoSeleccionado] = useState<string>('whatsapp');
  const [notasPedido, setNotasPedido] = useState('');
  const [productosCarrito, setProductosCarrito] = useState<Array<{producto: Producto, cantidad: number}>>([]);
  const [busquedaProducto, setBusquedaProducto] = useState('');
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;

  // Función para generar y descargar boleta PDF
  const descargarBoleta = (pedido: Pedido) => {
    setGenerandoBoleta(true);
    
    // Crear contenido HTML de la boleta
    const fechaPedido = new Date(pedido.fecha_pedido).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const itemsRows = pedido.items?.map(item => `
      <tr>
        <td style="padding: 8px; border-bottom: 1px solid #eee;">${item.producto_detalle?.nombre || item.producto_nombre || 'Producto'}</td>
        <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: center;">${item.cantidad}</td>
        <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(item.precio_unitario)}</td>
        <td style="padding: 8px; border-bottom: 1px solid #eee; text-align: right; font-weight: 600;">${formatCurrency(item.precio_total)}</td>
      </tr>
    `).join('') || '';

    const boletaHTML = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Boleta ${pedido.numero_pedido} - El Piñón</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Arial, sans-serif; 
            padding: 40px; 
            background: #fff;
            color: #333;
          }
          .boleta-container {
            max-width: 800px;
            margin: 0 auto;
            border: 2px solid #2e5a33;
            border-radius: 12px;
            overflow: hidden;
          }
          .header {
            background: linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%);
            color: white;
            padding: 30px;
            text-align: center;
          }
          .header h1 {
            font-size: 28px;
            margin-bottom: 5px;
          }
          .header p {
            opacity: 0.9;
            font-size: 14px;
          }
          .boleta-info {
            display: flex;
            justify-content: space-between;
            padding: 20px 30px;
            background: #f8fffe;
            border-bottom: 1px solid #e8f5e8;
          }
          .boleta-info div {
            text-align: center;
          }
          .boleta-info label {
            font-size: 12px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .boleta-info p {
            font-size: 16px;
            font-weight: 600;
            color: #2e5a33;
            margin-top: 5px;
          }
          .cliente-info {
            padding: 20px 30px;
            background: #fff;
            border-bottom: 1px solid #e8f5e8;
          }
          .cliente-info h3 {
            color: #2e5a33;
            margin-bottom: 15px;
            font-size: 16px;
          }
          .cliente-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }
          .cliente-grid div {
            font-size: 14px;
          }
          .cliente-grid label {
            color: #666;
            font-size: 12px;
          }
          .productos {
            padding: 20px 30px;
          }
          .productos h3 {
            color: #2e5a33;
            margin-bottom: 15px;
            font-size: 16px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          thead tr {
            background: #f8fffe;
          }
          th {
            padding: 12px 8px;
            text-align: left;
            font-size: 12px;
            text-transform: uppercase;
            color: #666;
            border-bottom: 2px solid #2e5a33;
          }
          th:nth-child(2), th:nth-child(3), th:nth-child(4) {
            text-align: center;
          }
          th:last-child {
            text-align: right;
          }
          .total-section {
            padding: 20px 30px;
            background: linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%);
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .total-section span {
            color: white;
            font-size: 18px;
          }
          .total-section .monto {
            font-size: 28px;
            font-weight: 700;
          }
          .footer {
            padding: 20px 30px;
            text-align: center;
            background: #f8fffe;
            font-size: 12px;
            color: #666;
          }
          .footer p {
            margin: 5px 0;
          }
          .estado-badge {
            display: inline-block;
            padding: 5px 15px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
          }
          @media print {
            body { padding: 0; }
            .boleta-container { border: none; }
          }
        </style>
      </head>
      <body>
        <div class="boleta-container">
          <!-- Header -->
          <div class="header">
            <h1>🌲 El Piñón</h1>
            <p>Boleta de Compra</p>
          </div>

          <!-- Info de Boleta -->
          <div class="boleta-info">
            <div>
              <label>Nº Pedido</label>
              <p>${pedido.numero_pedido}</p>
            </div>
            <div>
              <label>Fecha</label>
              <p>${fechaPedido}</p>
            </div>
            <div>
              <label>Estado</label>
              <p>${pedido.estado_display || pedido.estado}</p>
            </div>
            <div>
              <label>Método de Pago</label>
              <p>${(pedido as any).metodo_pago_display || pedido.metodo_pago}</p>
            </div>
          </div>

          <!-- Datos del Cliente -->
          <div class="cliente-info">
            <h3>📋 Datos del Cliente</h3>
            <div class="cliente-grid">
              <div>
                <label>Nombre Completo</label>
                <p>${pedido.cliente_detalle ? `${pedido.cliente_detalle.nombre || ''} ${pedido.cliente_detalle.apellido || ''}`.trim() || 'Cliente' : 'Cliente'}</p>
              </div>
              <div>
                <label>Email</label>
                <p>${pedido.cliente_detalle?.email || '-'}</p>
              </div>
              <div>
                <label>Teléfono</label>
                <p>${pedido.direccion_entrega_detalle?.telefono_contacto || pedido.cliente_detalle?.telefono || '-'}</p>
              </div>
              <div>
                <label>RUT/DNI</label>
                <p>${pedido.cliente_detalle?.ruc || pedido.cliente_detalle?.dni || '-'}</p>
              </div>
              <div style="grid-column: span 2;">
                <label>Dirección de Entrega</label>
                <p>${pedido.direccion_entrega_detalle ? `${pedido.direccion_entrega_detalle.direccion_linea1}${pedido.direccion_entrega_detalle.direccion_linea2 ? ', ' + pedido.direccion_entrega_detalle.direccion_linea2 : ''}, ${pedido.direccion_entrega_detalle.distrito || ''} ${pedido.direccion_entrega_detalle.ciudad}`.trim() : '-'}</p>
              </div>
            </div>
          </div>

          <!-- Productos -->
          <div class="productos">
            <h3>🛒 Detalle de Productos</h3>
            <table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cantidad</th>
                  <th>Precio Unit.</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                ${itemsRows}
              </tbody>
            </table>
          </div>

          <!-- Total -->
          <div class="total-section">
            <span>TOTAL A PAGAR</span>
            <span class="monto">${formatCurrency(pedido.total)}</span>
          </div>

          <!-- Footer -->
          <div class="footer">
            <p><strong>Gracias por tu compra en El Piñón</strong></p>
            <p>www.elpinon.almacenlocal.com</p>
            ${pedido.codigo_seguimiento ? `<p>Código de seguimiento: ${pedido.codigo_seguimiento}</p>` : ''}
          </div>
        </div>
      </body>
      </html>
    `;

    // Crear ventana de impresión
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(boletaHTML);
      printWindow.document.close();
      
      // Esperar a que cargue y luego imprimir
      printWindow.onload = () => {
        printWindow.print();
        setGenerandoBoleta(false);
      };
      
      // Fallback si onload no funciona
      setTimeout(() => {
        setGenerandoBoleta(false);
      }, 2000);
    } else {
      setGenerandoBoleta(false);
      alert('Por favor habilita las ventanas emergentes para descargar la boleta');
    }
  };

  // Cargar pedidos desde la API
  const cargarPedidos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPedidos();
      setPedidos(data);
    } catch (err: any) {
      console.error('Error al cargar pedidos:', err);
      setError(err.message || 'Error al cargar los pedidos');
    } finally {
      setLoading(false);
    }
  };

  // Cargar productos disponibles
  const cargarProductos = async () => {
    try {
      const data = await productoService.getProductos({ activo: true });
      setProductosDisponibles(data);
    } catch (err: any) {
      console.error('Error al cargar productos:', err);
    }
  };

  // Cargar clientes disponibles
  const cargarClientes = async () => {
    try {
      const data = await getClientes();
      setClientesDisponibles(data);
    } catch (err: any) {
      console.error('Error al cargar clientes:', err);
    }
  };

  // Cargar direcciones del cliente seleccionado
  const cargarDireccionesCliente = async (clienteId: number) => {
    try {
      // Obtener el cliente para conseguir su email
      const cliente = clientesDisponibles.find(c => c.id === clienteId);
      if (!cliente || !cliente.email) {
        setError('El cliente seleccionado no tiene un email registrado');
        setDireccionesCliente([]);
        return;
      }

      const data = await getDireccionesAuthCliente(cliente.email);
      setDireccionesCliente(data);
      console.log('📍 Direcciones auth_app cargadas para cliente', cliente.email, ':', data);
      
      if (data.length === 0) {
        setError('El cliente seleccionado no tiene direcciones registradas. Por favor, registra una dirección primero.');
      } else {
        setError(null);
      }
    } catch (err: any) {
      console.error('Error al cargar direcciones:', err);
      setError('Error al cargar las direcciones del cliente');
    }
  };

  // Abrir diálogo de crear pedido
  const handleAbrirCrearPedido = () => {
    setDialogCrearPedido(true);
    cargarProductos();
    cargarClientes();
  };

  // Agregar producto al carrito
  const agregarProductoCarrito = (producto: Producto) => {
    const existe = productosCarrito.find(p => p.producto.id === producto.id);
    if (existe) {
      setProductosCarrito(productosCarrito.map(p => 
        p.producto.id === producto.id 
          ? { ...p, cantidad: p.cantidad + 1 }
          : p
      ));
    } else {
      setProductosCarrito([...productosCarrito, { producto, cantidad: 1 }]);
    }
  };

  // Modificar cantidad de producto en carrito
  const modificarCantidadCarrito = (productoId: number, cantidad: number) => {
    if (cantidad <= 0) {
      setProductosCarrito(productosCarrito.filter(p => p.producto.id !== productoId));
    } else {
      setProductosCarrito(productosCarrito.map(p => 
        p.producto.id === productoId 
          ? { ...p, cantidad }
          : p
      ));
    }
  };

  // Calcular total del carrito
  const calcularTotalCarrito = () => {
    return productosCarrito.reduce((total, item) => 
      total + (parseFloat(item.producto.precio_venta.toString()) * item.cantidad), 0
    );
  };

  // Crear pedido
  const handleCrearPedido = async () => {
    // Validación mejorada: verificar que sean números válidos, no solo truthy
    if (clienteSeleccionado === null || direccionSeleccionada === null) {
      setError('Por favor selecciona un cliente y una dirección de entrega');
      return;
    }
    
    if (productosCarrito.length === 0) {
      setError('Por favor agrega al menos un producto al pedido');
      return;
    }

    // Verificar que la dirección pertenezca al cliente seleccionado
    const direccionValida = direccionesCliente.find(d => d.id === direccionSeleccionada);
    if (!direccionValida) {
      setError('La dirección seleccionada no es válida para este cliente');
      return;
    }

    try {
      setCreandoPedido(true);
      setError(null);

      const itemsData = productosCarrito.map(item => ({
        producto: item.producto.id,
        cantidad: item.cantidad,
        precio_unitario: parseFloat(item.producto.precio_venta.toString())
      }));

      const pedidoData: CrearPedidoData = {
        items: itemsData,
        direccion_id: Number(direccionSeleccionada),
        cliente_id: Number(clienteSeleccionado),
        metodo_pago: metodoPagoSeleccionado as any,
        notas_cliente: notasPedido || 'Pedido creado manualmente desde dashboard'
      };

      console.log('📦 Datos del pedido a enviar:');
      console.log('- Cliente ID:', pedidoData.cliente_id, 'Tipo:', typeof pedidoData.cliente_id);
      console.log('- Cliente seleccionado:', clientesDisponibles.find(c => c.id === clienteSeleccionado));
      console.log('- Dirección ID:', pedidoData.direccion_id, 'Tipo:', typeof pedidoData.direccion_id);
      console.log('- Dirección válida:', direccionValida);
      console.log('- Direcciones disponibles:', direccionesCliente);
      console.log('- Items:', itemsData);
      console.log('- Pedido completo:', JSON.stringify(pedidoData, null, 2));
      
      await createPedido(pedidoData);
      
      // Resetear formulario
      setDialogCrearPedido(false);
      setClienteSeleccionado(null);
      setDireccionSeleccionada(null);
      setMetodoPagoSeleccionado('whatsapp');
      setNotasPedido('');
      setProductosCarrito([]);
      setBusquedaProducto('');
      
      // Recargar pedidos
      await cargarPedidos();
      
      setError(null);
    } catch (err: any) {
      console.error('Error al crear pedido:', err);
      console.error('Detalles del error:', err.response?.data); // Debug
      
      // Mostrar errores específicos del backend
      if (err.response?.data) {
        const errorData = err.response.data;
        const errorMessages = [];
        
        if (errorData.direccion_id) {
          errorMessages.push(`Dirección: ${Array.isArray(errorData.direccion_id) ? errorData.direccion_id.join(', ') : errorData.direccion_id}`);
        }
        if (errorData.cliente_id) {
          errorMessages.push(`Cliente: ${Array.isArray(errorData.cliente_id) ? errorData.cliente_id.join(', ') : errorData.cliente_id}`);
        }
        if (errorData.items) {
          errorMessages.push(`Items: ${Array.isArray(errorData.items) ? errorData.items.join(', ') : errorData.items}`);
        }
        if (errorData.detail) {
          errorMessages.push(errorData.detail);
        }
        
        if (errorMessages.length > 0) {
          setError(errorMessages.join(' | '));
        } else {
          setError(JSON.stringify(errorData));
        }
      } else {
        setError(err.message || 'Error al crear el pedido');
      }
    } finally {
      setCreandoPedido(false);
    }
  };

  // Cargar detalle completo de un pedido
  const cargarDetallePedido = async (pedidoId: number) => {
    try {
      setLoadingDetalle(true);
      const pedidoCompleto = await getPedidoById(pedidoId);
      setPedidoSeleccionado(pedidoCompleto);
      setNuevoEstado(pedidoCompleto.estado);
      setDialogDetalles(true);
    } catch (err: any) {
      console.error('Error al cargar detalle del pedido:', err);
      setError(err.message || 'Error al cargar los detalles del pedido');
    } finally {
      setLoadingDetalle(false);
    }
  };

  // Actualizar estado del pedido
  const actualizarEstadoPedido = async () => {
    if (!pedidoSeleccionado) return;

    try {
      setActualizandoEstado(true);
      setDialogConfirmacion(false);
      await cambiarEstadoPedido(pedidoSeleccionado.id, nuevoEstado);
      
      // Actualizar el pedido seleccionado
      setPedidoSeleccionado({
        ...pedidoSeleccionado,
        estado: nuevoEstado,
        estado_display: nuevoEstado.charAt(0).toUpperCase() + nuevoEstado.slice(1).replace('_', ' ')
      });

      // Recargar la lista de pedidos
      await cargarPedidos();
      
      setError(null);
    } catch (err: any) {
      console.error('Error al actualizar estado:', err);
      setError(err.message || 'Error al actualizar el estado del pedido');
    } finally {
      setActualizandoEstado(false);
    }
  };

  // Actualizar estado rápido desde la lista
  const actualizarEstadoRapido = async (pedidoId: number, nuevoEstadoParam: EstadoPedido) => {
    try {
      setPedidoEditando(pedidoId);
      await cambiarEstadoPedido(pedidoId, nuevoEstadoParam);
      
      // Recargar la lista de pedidos
      await cargarPedidos();
      
      setError(null);
      setMenuAnchorEl(null);
    } catch (err: any) {
      console.error('Error al actualizar estado:', err);
      setError(err.message || 'Error al actualizar el estado del pedido');
    } finally {
      setPedidoEditando(null);
    }
  };

  useEffect(() => {
    cargarPedidos();
  }, []);

  // Auto-refresh cada 30 segundos
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(cargarPedidos, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  // Auto-refresh cada 30 segundos
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(cargarPedidos, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  // Formatear precio
  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(numPrice);
  };

  // Calcular estadísticas
  const hoy = new Date().toDateString();
  const pedidosHoy = pedidos.filter(p => new Date(p.fecha_pedido).toDateString() === hoy);
  const pedidosEnPreparacion = pedidos.filter(p => p.estado === 'preparando' || p.estado === 'confirmado');
  const pedidosEnDelivery = pedidos.filter(p => p.estado === 'en_camino');
  const ventasDelDia = pedidosHoy.reduce((sum, p) => sum + parseFloat(p.total), 0);

  const estadosData = [
    {
      title: 'Pedidos Hoy',
      value: pedidosHoy.length.toString(),
      change: '+23%',
      trend: 'up',
      icon: <ShoppingCart />,
      color: '#2e5a33',
    },
    {
      title: 'En Preparación',
      value: pedidosEnPreparacion.length.toString(),
      change: '+12%',
      trend: 'up',
      icon: <Assignment />,
      color: '#ff8f00',
    },
    {
      title: 'En Delivery',
      value: pedidosEnDelivery.length.toString(),
      change: '+8%',
      trend: 'up',
      icon: <LocalShipping />,
      color: '#1976d2',
    },
    {
      title: 'Ventas del Día',
      value: formatPrice(ventasDelDia),
      change: '+35%',
      trend: 'up',
      icon: <AttachMoney />,
      color: '#2e7d32',
    },
  ];

  const getStatusColor = (estado: string) => {
    switch (estado) {
      case 'pendiente': return '#ff8f00';
      case 'confirmado': return '#1976d2';
      case 'preparando': return '#9c27b0';
      case 'enviado': return '#2196f3';
      case 'entregado': return '#4caf50';
      case 'cancelado': return '#f44336';
      default: return '#757575';
    }
  };

  const getStatusIcon = (estado: string) => {
    switch (estado) {
      case 'pendiente': return <Pending />;
      case 'confirmado': return <CheckCircle />;
      case 'preparando': return <Inventory2 />;
      case 'enviado': return <LocalShipping />;
      case 'entregado': return <CheckCircle />;
      case 'cancelado': return <Cancel />;
      default: return <Assignment />;
    }
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(numAmount);
  };

  const formatTime = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStepperActiveStep = (estado: EstadoPedido) => {
    switch (estado) {
      case 'pendiente': return 0;
      case 'confirmado': return 1;
      case 'preparando': return 2;
      case 'en_camino': return 3;
      case 'entregado': return 4;
      default: return 0;
    }
  };

  const pedidosFiltrados = pedidos.filter(pedido => {
    const matchEstado = filtroEstado === 'todos' || pedido.estado === filtroEstado;
    const matchBusqueda = busqueda === '' || 
      pedido.numero_pedido.toLowerCase().includes(busqueda.toLowerCase()) ||
      (pedido.cliente_detalle?.nombre || '').toLowerCase().includes(busqueda.toLowerCase()) ||
      (pedido.cliente_detalle?.email || '').toLowerCase().includes(busqueda.toLowerCase());
    return matchEstado && matchBusqueda;
  });

  // Calcular paginación
  const totalPages = Math.ceil(pedidosFiltrados.length / ITEMS_PER_PAGE);
  const paginatedPedidos = pedidosFiltrados.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // Resetear página cuando cambia el filtro
  useEffect(() => {
    setCurrentPage(1);
  }, [filtroEstado, busqueda]);

  const steps = ['Pendiente', 'Confirmado', 'Preparando', 'En Camino', 'Entregado'];

  return (
    <DashboardContainer>
      {/* Header */}
      <HeaderSection>
        <Box display="flex" alignItems="center" gap={2} sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <Avatar
            src="/assets/logos/logo-el-pinon-nuevo.png"
            sx={{ width: { xs: 48, sm: 60 }, height: { xs: 48, sm: 60 }, border: '3px solid rgba(255,255,255,0.3)' }}
          />
          <Box>
            <Typography variant="h4" fontWeight={700} fontFamily="'Playfair Display', serif" sx={{ fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' } }}>
              Pedidos Online - El Piñón
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Gestión completa de pedidos y entregas
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2} alignItems="center" sx={{ flexWrap: 'wrap', width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'space-between', md: 'flex-end' } }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#fff',
                  },
                }}
              />
            }
            label="Auto-refresh"
            sx={{ color: 'white' }}
          />
          
          <ActionButton
            startIcon={<Add />}
            onClick={handleAbrirCrearPedido}
            sx={{ backgroundColor: 'rgba(255,255,255,0.2)', fontSize: { xs: '0.875rem', sm: '1rem' }, padding: { xs: '8px 16px', sm: '12px 24px' } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Nuevo </Box>Pedido
          </ActionButton>
        </Box>
      </HeaderSection>

      {/* Stats Cards */}
      <Box display="flex" gap={{ xs: 2, sm: 2.5, md: 3 }} marginBottom={{ xs: 3, md: 4 }} flexWrap="wrap">
        {estadosData.map((stat, index) => (
          <Box key={index} flex="1 1 250px" sx={{ minWidth: { xs: 'calc(50% - 8px)', sm: 'calc(50% - 10px)', md: '250px' } }}>
            <StatsCard>
              <CardContent sx={{ padding: { xs: '16px', sm: '20px', md: '24px' } }}>
                <Box display="flex" justifyContent="space-between" alignItems="start">
                  <Box>
                    <Typography variant="h4" fontWeight={700} color={stat.color} sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" marginBottom={1} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {stat.title}
                    </Typography>
                    <Chip
                      label={stat.change}
                      size="small"
                      sx={{
                        backgroundColor: stat.trend === 'up' ? '#e8f5e9' : '#fff3e0',
                        color: stat.trend === 'up' ? '#2e7d32' : '#f57c00',
                        fontWeight: 600,
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      }}
                    />
                  </Box>
                  <Avatar
                    sx={{
                      backgroundColor: stat.color,
                      color: 'white',
                      width: { xs: 40, sm: 48, md: 56 },
                      height: { xs: 40, sm: 48, md: 56 },
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </StatsCard>
          </Box>
        ))}
      </Box>

      {/* Filters */}
      <FilterCard>
        <CardContent sx={{ padding: { xs: '16px', sm: '20px', md: '24px' } }}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              placeholder="Buscar pedidos..."
              variant="outlined"
              size="small"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: '#2e5a33' }} />
              }}
              sx={{
                minWidth: { xs: '100%', sm: '300px' },
                flex: { xs: '1 1 100%', sm: '0 1 auto' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
            <Box display="flex" gap={1} flexWrap="wrap" sx={{ flex: 1 }}>
              {['todos', 'pendiente', 'confirmado', 'preparando', 'enviado', 'entregado'].map((estado) => (
                <Chip
                  key={estado}
                  label={estado === 'todos' ? 'Todos' : estado.charAt(0).toUpperCase() + estado.slice(1)}
                  onClick={() => setFiltroEstado(estado)}
                  variant={filtroEstado === estado ? 'filled' : 'outlined'}
                  sx={{
                    backgroundColor: filtroEstado === estado ? '#2e5a33' : 'transparent',
                    color: filtroEstado === estado ? 'white' : '#2e5a33',
                    borderColor: '#2e5a33',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&:hover': {
                      backgroundColor: filtroEstado === estado ? '#3d7342' : 'rgba(46, 90, 51, 0.1)',
                    },
                  }}
                />
              ))}
            </Box>
            <IconButton
              onClick={() => window.location.reload()}
              sx={{ color: '#2e5a33' }}
            >
              <Refresh />
            </IconButton>
          </Box>
        </CardContent>
      </FilterCard>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            marginBottom: 3,
            borderRadius: '12px',
          }}
        >
          {error}
        </Alert>
      )}

      {/* Alert de pedidos urgentes */}
      {!loading && pedidos.filter(p => p.estado === 'pendiente').length > 0 && (
        <Alert
          severity="warning"
          sx={{
            marginBottom: 3,
            borderRadius: '12px',
            backgroundColor: '#fff3e0',
            border: '1px solid #ff8f00',
          }}
          icon={<NotificationImportant />}
        >
          <Typography variant="body2">
            Tienes {pedidos.filter(p => p.estado === 'pendiente').length} pedidos pendientes de confirmación.
          </Typography>
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} sx={{ color: '#2e5a33' }} />
        </Box>
      ) : (
        <>
      {/* Orders List */}
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={{ xs: 2, md: 3 }}>
          <Typography variant="h6" fontWeight={600} color="#2e5a33" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Pedidos Recientes ({pedidosFiltrados.length})
          </Typography>
        </Box>

        {paginatedPedidos.map((pedido) => (
          <OrderCard key={pedido.id}>
            <CardContent sx={{ padding: { xs: '16px', sm: '20px', md: '24px' } }}>
              <Box display="flex" justifyContent="space-between" alignItems="start" marginBottom={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
                <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 2 }} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Avatar
                    sx={{
                      backgroundColor: getStatusColor(pedido.estado) + '20',
                      color: getStatusColor(pedido.estado),
                      width: { xs: 40, sm: 44, md: 48 },
                      height: { xs: 40, sm: 44, md: 48 },
                    }}
                  >
                    {getStatusIcon(pedido.estado)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                      {pedido.numero_pedido}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {pedido.cliente_detalle?.nombre || 'Cliente'} • {formatTime(pedido.fecha_pedido)}
                    </Typography>
                    <Box display="flex" gap={1} marginTop={1} flexWrap="wrap">
                      <Chip
                        label={pedido.estado_display}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(pedido.estado) + '20',
                          color: getStatusColor(pedido.estado),
                          fontWeight: 600,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        }}
                      />
                      <Chip
                        label={pedido.metodo_pago.toUpperCase()}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: '#e8f5e8', color: '#666', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box textAlign="right" display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#2e5a33" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                      {formatCurrency(pedido.total)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {pedido.items?.length || 0} productos
                    </Typography>
                  </Box>
                  <Box display="flex" gap={{ xs: 0.5, sm: 1 }}>
                    <IconButton
                      onClick={() => descargarBoleta(pedido)}
                      title="Descargar Boleta"
                      size="small"
                      sx={{
                        color: '#9c27b0',
                        padding: { xs: '6px', sm: '8px' },
                        '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.1)' },
                      }}
                    >
                      <Receipt fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => cargarDetallePedido(pedido.id)}
                      disabled={loadingDetalle}
                      title="Ver Detalles"
                      size="small"
                      sx={{
                        color: '#2e5a33',
                        padding: { xs: '6px', sm: '8px' },
                        '&:hover': { backgroundColor: 'rgba(46, 90, 51, 0.1)' },
                      }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        setMenuAnchorEl(e.currentTarget);
                        setPedidoEditando(pedido.id);
                      }}
                      disabled={pedidoEditando === pedido.id}
                      title="Cambiar Estado"
                      size="small"
                      sx={{
                        color: '#1976d2',
                        padding: { xs: '6px', sm: '8px' },
                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                      }}
                    >
                      {pedidoEditando === pedido.id ? <CircularProgress size={16} /> : <MoreVert fontSize="small" />}
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {/* Menú de cambio rápido de estado */}
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl) && pedidoEditando === pedido.id}
                onClose={() => {
                  setMenuAnchorEl(null);
                  setPedidoEditando(null);
                }}
              >
                <MenuItem onClick={() => actualizarEstadoRapido(pedido.id, 'pendiente')}>
                  <ListItemIcon><Pending fontSize="small" sx={{ color: '#ff8f00' }} /></ListItemIcon>
                  Pendiente
                </MenuItem>
                <MenuItem onClick={() => actualizarEstadoRapido(pedido.id, 'confirmado')}>
                  <ListItemIcon><CheckCircle fontSize="small" sx={{ color: '#1976d2' }} /></ListItemIcon>
                  Confirmado
                </MenuItem>
                <MenuItem onClick={() => actualizarEstadoRapido(pedido.id, 'preparando')}>
                  <ListItemIcon><Inventory2 fontSize="small" sx={{ color: '#9c27b0' }} /></ListItemIcon>
                  Preparando
                </MenuItem>
                <MenuItem onClick={() => actualizarEstadoRapido(pedido.id, 'en_camino')}>
                  <ListItemIcon><LocalShipping fontSize="small" sx={{ color: '#2196f3' }} /></ListItemIcon>
                  En Camino
                </MenuItem>
                <MenuItem onClick={() => actualizarEstadoRapido(pedido.id, 'entregado')}>
                  <ListItemIcon><CheckCircle fontSize="small" sx={{ color: '#4caf50' }} /></ListItemIcon>
                  Entregado
                </MenuItem>
                <MenuItem onClick={() => actualizarEstadoRapido(pedido.id, 'cancelado')}>
                  <ListItemIcon><Cancel fontSize="small" sx={{ color: '#f44336' }} /></ListItemIcon>
                  Cancelado
                </MenuItem>
              </Menu>

              {/* Customer Info */}
              <Box 
                sx={{
                  display: 'flex', 
                  gap: { xs: 1.5, sm: 2, md: 3 }, 
                  marginBottom: 2, 
                  padding: { xs: 1.5, sm: 2 }, 
                  bgcolor: 'rgba(46, 90, 51, 0.05)', 
                  borderRadius: '12px',
                  flexWrap: 'wrap'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <Person sx={{ color: '#666', fontSize: { xs: 16, sm: 18 } }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {pedido.cliente_detalle?.nombre || pedido.cliente_detalle?.email || 'Cliente'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: { xs: '48%', sm: 'auto' } }}>
                  <Phone sx={{ color: '#666', fontSize: { xs: 16, sm: 18 } }} />
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {pedido.direccion_entrega_detalle?.telefono_contacto || 'Ver detalles'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: { xs: '100%', sm: 'auto' }, flex: { sm: 1 } }}>
                  <LocationOn sx={{ color: '#666', fontSize: { xs: 16, sm: 18 } }} />
                  <Typography variant="body2" sx={{ maxWidth: { xs: '100%', sm: '300px' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {pedido.direccion_entrega_detalle 
                      ? `${pedido.direccion_entrega_detalle.direccion_linea1}, ${pedido.direccion_entrega_detalle.ciudad}`
                      : 'Ver detalles'
                    }
                  </Typography>
                </Box>
              </Box>

              {/* Products Summary */}
              <Box>
                <Typography variant="subtitle2" marginBottom={1} color="#2e5a33" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Productos:
                </Typography>
                {pedido.items && pedido.items.length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {pedido.items.slice(0, 3).map((item) => (
                      <Chip
                        key={item.id}
                        label={`${item.producto_detalle?.nombre || item.producto_nombre || 'Producto'} (${item.cantidad})`}
                        size="small"
                        sx={{
                          backgroundColor: '#f8fffe',
                          color: '#2e5a33',
                          border: '1px solid #e8f5e8',
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        }}
                      />
                    ))}
                    {pedido.items.length > 3 && (
                      <Chip
                        label={`+${pedido.items.length - 3} más...`}
                        size="small"
                        onClick={() => cargarDetallePedido(pedido.id)}
                        sx={{
                          backgroundColor: '#2e5a33',
                          color: 'white',
                          border: '1px solid #2e5a33',
                          cursor: 'pointer',
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          '&:hover': {
                            backgroundColor: '#3d7342',
                          },
                        }}
                      />
                    )}
                  </Box>
                ) : (
                  <Chip
                    label="Ver detalles de productos"
                    size="small"
                    icon={<Visibility />}
                    onClick={() => cargarDetallePedido(pedido.id)}
                    sx={{
                      backgroundColor: '#e8f5e8',
                      color: '#2e5a33',
                      border: '1px solid #2e5a33',
                      cursor: 'pointer',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      '&:hover': {
                        backgroundColor: '#2e5a33',
                        color: 'white',
                      },
                    }}
                  />
                )}
              </Box>
            </CardContent>
          </OrderCard>
        ))}

        {/* Paginación */}
        {pedidosFiltrados.length > ITEMS_PER_PAGE && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, py: 3, gap: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, pedidosFiltrados.length)} de {pedidosFiltrados.length} pedidos
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              siblingCount={0}
              boundaryCount={1}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minWidth: { xs: '28px', sm: '32px' },
                  height: { xs: '28px', sm: '32px' },
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
                    color: 'white',
                  },
                },
              }}
            />
          </Box>
        )}

        {pedidosFiltrados.length === 0 && (
          <Box p={6} textAlign="center">
            <Avatar
              sx={{
                width: 80,
                height: 80,
                backgroundColor: '#e8f5e8',
                color: '#2e5a33',
                margin: '0 auto 16px',
              }}
            >
              <ShoppingCart sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h5" fontWeight={600} color="#2e5a33" marginBottom={2}>
              No se encontraron pedidos
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {busqueda || filtroEstado !== 'todos' 
                ? 'Ajusta los filtros para ver más resultados' 
                : 'No hay pedidos registrados'
              }
            </Typography>
          </Box>
        )}
      </Box>
      </>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="refresh"
        onClick={cargarPedidos}
        size="medium"
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24, md: 32 },
          right: { xs: 16, sm: 24, md: 32 },
          background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 },
          '&:hover': {
            background: 'linear-gradient(135deg, #3d7342 0%, #2e5a33 100%)',
            transform: 'scale(1.1)',
          },
        }}
      >
        <Refresh />
      </Fab>

      {/* Order Details Dialog */}
      <Dialog
        open={dialogDetalles}
        onClose={() => setDialogDetalles(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: { xs: '90vh', sm: '85vh' },
            margin: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
          }
        }}
      >
        {loadingDetalle ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={60} sx={{ color: '#2e5a33' }} />
          </Box>
        ) : pedidoSeleccionado && (
          <>
            <DialogTitle sx={{ padding: { xs: '16px', sm: '20px', md: '24px' } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}>
                  Detalles del Pedido {pedidoSeleccionado.numero_pedido}
                </Typography>
                <Chip
                  label={pedidoSeleccionado.estado_display}
                  sx={{
                    backgroundColor: getStatusColor(pedidoSeleccionado.estado) + '20',
                    color: getStatusColor(pedidoSeleccionado.estado),
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                />
              </Box>
            </DialogTitle>
            <DialogContent sx={{ padding: { xs: '16px', sm: '20px', md: '24px' } }}>
              {/* Order Progress */}
              <Box marginBottom={{ xs: 2, sm: 3 }}>
                <Typography variant="h6" marginBottom={2} color="#2e5a33" sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                  Estado del Pedido
                </Typography>
                <Stepper activeStep={getStepperActiveStep(pedidoSeleccionado.estado)} alternativeLabel sx={{ 
                  '& .MuiStepLabel-label': { 
                    fontSize: { xs: '0.7rem', sm: '0.875rem' } 
                  } 
                }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
                
                {/* Selector de Estado */}
                <Box marginTop={{ xs: 2, sm: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Cambiar Estado</InputLabel>
                    <Select
                      value={nuevoEstado}
                      label="Cambiar Estado"
                      onChange={(e) => setNuevoEstado(e.target.value as EstadoPedido)}
                      disabled={actualizandoEstado}
                    >
                      <MenuItem value="pendiente">Pendiente</MenuItem>
                      <MenuItem value="confirmado">Confirmado</MenuItem>
                      <MenuItem value="preparando">Preparando</MenuItem>
                      <MenuItem value="en_camino">En Camino</MenuItem>
                      <MenuItem value="entregado">Entregado</MenuItem>
                      <MenuItem value="cancelado">Cancelado</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Divider sx={{ marginBottom: { xs: 2, sm: 3 } }} />

              {/* Customer Details */}
              <Box marginBottom={{ xs: 2, sm: 3 }}>
                <Typography variant="h6" marginBottom={2} color="#2e5a33" sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                  Información del Cliente
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography><strong>Nombre:</strong> {pedidoSeleccionado.cliente_detalle?.nombre || 'Cliente'}</Typography>
                  <Typography><strong>Email:</strong> {pedidoSeleccionado.cliente_detalle?.email || 'Sin email'}</Typography>
                  <Typography><strong>Teléfono:</strong> {pedidoSeleccionado.direccion_entrega_detalle?.telefono_contacto || 'Sin teléfono'}</Typography>
                  <Typography><strong>Dirección:</strong> {
                    pedidoSeleccionado.direccion_entrega_detalle 
                      ? `${pedidoSeleccionado.direccion_entrega_detalle.direccion_linea1}, ${pedidoSeleccionado.direccion_entrega_detalle.ciudad}`
                      : 'Sin dirección'
                  }</Typography>
                </Box>
              </Box>

              <Divider sx={{ marginBottom: 3 }} />

              {/* Products */}
              <Box marginBottom={3}>
                <Typography variant="h6" marginBottom={2} color="#2e5a33">
                  Productos Pedidos
                </Typography>
                <List>
                  {pedidoSeleccionado.items?.map((item) => (
                    <ListItem key={item.id}>
                      <ListItemAvatar>
                        <Avatar 
                          src={item.producto_detalle?.imagen}
                          sx={{ backgroundColor: '#e8f5e8', color: '#2e5a33' }}
                        >
                          <Inventory2 />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={item.producto_detalle?.nombre || 'Producto'}
                        secondary={`Cantidad: ${item.cantidad} • Precio: ${formatCurrency(item.precio_unitario)}`}
                        primaryTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      />
                      <Typography variant="h6" color="#2e5a33" sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                        {formatCurrency(item.precio_total)}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
                <Divider />
                <Box display="flex" justifyContent="space-between" padding={{ xs: 1.5, sm: 2 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                    Total:
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="#2e5a33" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}>
                    {formatCurrency(pedidoSeleccionado.total)}
                  </Typography>
                </Box>
              </Box>

              {pedidoSeleccionado.notas_cliente && (
                <Box>
                  <Typography variant="h6" marginBottom={1} color="#2e5a33" sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                    Notas Especiales
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {pedidoSeleccionado.notas_cliente}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ padding: { xs: '12px 16px', sm: '16px 20px', md: '20px 24px' }, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
              <Button 
                onClick={() => pedidoSeleccionado && descargarBoleta(pedidoSeleccionado)}
                disabled={generandoBoleta}
                startIcon={generandoBoleta ? <CircularProgress size={16} /> : <Receipt />}
                sx={{ 
                  color: '#2e5a33',
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': { backgroundColor: 'rgba(46, 90, 51, 0.1)' }
                }}
              >
                {generandoBoleta ? 'Generando...' : 'Descargar Boleta'}
              </Button>
              <Button onClick={() => setDialogDetalles(false)} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                Cerrar
              </Button>
              <ActionButton 
                onClick={() => setDialogConfirmacion(true)}
                disabled={actualizandoEstado || nuevoEstado === pedidoSeleccionado.estado}
                sx={{ width: { xs: '100%', sm: 'auto' }, fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Actualizar Estado
              </ActionButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Diálogo de confirmación para cambio de estado */}
      <Dialog
        open={dialogConfirmacion}
        onClose={() => setDialogConfirmacion(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Cambio de Estado</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de cambiar el estado del pedido <strong>{pedidoSeleccionado?.numero_pedido}</strong> a <strong>{nuevoEstado.toUpperCase().replace('_', ' ')}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogConfirmacion(false)}>
            Cancelar
          </Button>
          <ActionButton 
            onClick={actualizarEstadoPedido}
            disabled={actualizandoEstado}
          >
            {actualizandoEstado ? <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} /> : null}
            {actualizandoEstado ? 'Actualizando...' : 'Confirmar'}
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Diálogo para crear nuevo pedido */}
      <Dialog
        open={dialogCrearPedido}
        onClose={() => !creandoPedido && setDialogCrearPedido(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={false}
        PaperProps={{
          sx: {
            maxHeight: { xs: '90vh', sm: '85vh' },
            margin: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
          }
        }}
      >
        <DialogTitle sx={{ padding: { xs: '16px', sm: '20px', md: '24px' } }}>
          <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 2 }}>
            <Avatar sx={{ backgroundColor: '#2e5a33', color: 'white', width: { xs: 36, sm: 40, md: 48 }, height: { xs: 36, sm: 40, md: 48 } }}>
              <Add />
            </Avatar>
            <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}>
              Crear Nuevo Pedido Manual
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ padding: { xs: '16px', sm: '20px', md: '24px' } }}>
          <Box sx={{ pt: { xs: 1, sm: 2 } }}>
            {/* Selección de Cliente */}
            <Box marginBottom={{ xs: 2, sm: 3 }}>
              <Typography variant="h6" marginBottom={2} color="#2e5a33" sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                1. Seleccionar Cliente
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Cliente</InputLabel>
                <Select
                  value={clienteSeleccionado || ''}
                  label="Cliente"
                  onChange={(e) => {
                    const clienteId = e.target.value as number;
                    setClienteSeleccionado(clienteId);
                    setDireccionSeleccionada(null);
                    if (clienteId) {
                      cargarDireccionesCliente(clienteId);
                    }
                  }}
                  disabled={creandoPedido}
                >
                  {clientesDisponibles.map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      <Box>
                        <Typography variant="body1">
                          {cliente.nombre} {cliente.apellido}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cliente.email}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Selección de Dirección */}
            {clienteSeleccionado && (
              <Box marginBottom={{ xs: 2, sm: 3 }}>
                <Typography variant="h6" marginBottom={2} color="#2e5a33" sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                  2. Seleccionar Dirección de Entrega
                </Typography>
                {direccionesCliente.length === 0 ? (
                  <Alert severity="warning" sx={{ borderRadius: '12px', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Este cliente no tiene direcciones registradas. Por favor, registra una dirección antes de crear el pedido.
                  </Alert>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel>Dirección</InputLabel>
                    <Select
                      value={direccionSeleccionada || ''}
                      label="Dirección"
                      onChange={(e) => setDireccionSeleccionada(e.target.value as number)}
                      disabled={creandoPedido}
                    >
                      {direccionesCliente.map((direccion) => (
                        <MenuItem key={direccion.id} value={direccion.id}>
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {direccion.apodo ? `${direccion.apodo} - ` : ''}{direccion.calle} {direccion.numero}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {direccion.ciudad}{direccion.comuna ? `, ${direccion.comuna}` : ''}{direccion.telefono ? ` - Tel: ${direccion.telefono}` : ''}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}

            {/* Selección de Productos */}
            <Box marginBottom={{ xs: 2, sm: 3 }}>
              <Typography variant="h6" marginBottom={2} color="#2e5a33" sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                3. Agregar Productos
              </Typography>
              
              {/* Búsqueda de productos */}
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar productos..."
                value={busquedaProducto}
                onChange={(e) => setBusquedaProducto(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: '#2e5a33' }} />
                }}
                sx={{ marginBottom: 2 }}
                disabled={creandoPedido}
              />
              
              {/* Lista de productos disponibles */}
              <Box sx={{ 
                maxHeight: { xs: '150px', sm: '200px' }, 
                overflowY: 'auto', 
                border: '1px solid #e8f5e8', 
                borderRadius: '12px',
                marginBottom: 2
              }}>
                {productosDisponibles
                  .filter(p => 
                    p.nombre.toLowerCase().includes(busquedaProducto.toLowerCase()) &&
                    p.stock_actual > 0
                  )
                  .map((producto) => (
                    <ListItem
                      key={producto.id}
                      sx={{
                        borderBottom: '1px solid #e8f5e8',
                        padding: { xs: '8px', sm: '12px' },
                        '&:hover': { backgroundColor: 'rgba(46, 90, 51, 0.05)' }
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: { xs: 40, sm: 56 } }}>
                        <Avatar src={producto.imagen} sx={{ backgroundColor: '#e8f5e8', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                          <Inventory2 sx={{ color: '#2e5a33', fontSize: { xs: 16, sm: 20 } }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={producto.nombre}
                        secondary={`Stock: ${producto.stock_actual} • ${formatCurrency(producto.precio_venta)}`}
                        primaryTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        secondaryTypographyProps={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      />
                      <IconButton
                        onClick={() => agregarProductoCarrito(producto)}
                        sx={{ color: '#2e5a33', padding: { xs: '6px', sm: '8px' } }}
                        disabled={creandoPedido}
                      >
                        <Add />
                      </IconButton>
                    </ListItem>
                  ))}
              </Box>

              {/* Carrito de productos */}
              {productosCarrito.length > 0 && (
                <Box sx={{ 
                  border: '2px solid #2e5a33', 
                  borderRadius: '12px', 
                  padding: 2,
                  backgroundColor: '#f8fffe'
                }}>
                  <Typography variant="subtitle1" fontWeight={600} marginBottom={2}>
                    🛒 Productos en el Pedido ({productosCarrito.length})
                  </Typography>
                  <List>
                    {productosCarrito.map((item) => (
                      <ListItem
                        key={item.producto.id}
                        sx={{ borderBottom: '1px solid #e8f5e8' }}
                      >
                        <ListItemAvatar>
                          <Avatar src={item.producto.imagen} sx={{ backgroundColor: '#e8f5e8' }}>
                            <Inventory2 sx={{ color: '#2e5a33' }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={item.producto.nombre}
                          secondary={formatCurrency(item.producto.precio_venta)}
                        />
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() => modificarCantidadCarrito(item.producto.id, item.cantidad - 1)}
                            disabled={creandoPedido}
                          >
                            <Remove />
                          </IconButton>
                          <Typography fontWeight={600} sx={{ minWidth: '40px', textAlign: 'center' }}>
                            {item.cantidad}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => modificarCantidadCarrito(item.producto.id, item.cantidad + 1)}
                            disabled={creandoPedido || item.cantidad >= item.producto.stock_actual}
                          >
                            <Add />
                          </IconButton>
                          <Typography fontWeight={700} color="#2e5a33" sx={{ minWidth: '100px', textAlign: 'right' }}>
                            {formatCurrency(parseFloat(item.producto.precio_venta.toString()) * item.cantidad)}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => modificarCantidadCarrito(item.producto.id, 0)}
                            sx={{ color: '#f44336' }}
                            disabled={creandoPedido}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 2 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={700}>
                      TOTAL:
                    </Typography>
                    <Typography variant="h5" fontWeight={700} color="#2e5a33">
                      {formatCurrency(calcularTotalCarrito())}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Método de Pago */}
            <Box marginBottom={3}>
              <Typography variant="h6" marginBottom={2} color="#2e5a33">
                4. Método de Pago
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Método de Pago</InputLabel>
                <Select
                  value={metodoPagoSeleccionado}
                  label="Método de Pago"
                  onChange={(e) => setMetodoPagoSeleccionado(e.target.value)}
                  disabled={creandoPedido}
                >
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="efectivo">Efectivo</MenuItem>
                  <MenuItem value="transferencia">Transferencia Bancaria</MenuItem>
                  <MenuItem value="tarjeta">Tarjeta</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Notas del Pedido */}
            <Box marginBottom={2}>
              <Typography variant="h6" marginBottom={2} color="#2e5a33">
                5. Notas Especiales (Opcional)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Ej: Cliente llamó por teléfono, pedido urgente, etc."
                value={notasPedido}
                onChange={(e) => setNotasPedido(e.target.value)}
                disabled={creandoPedido}
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ marginTop: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: { xs: '12px 16px', sm: '16px 20px', md: '20px 24px' }, gap: { xs: 1, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <Button 
            onClick={() => setDialogCrearPedido(false)}
            disabled={creandoPedido}
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Cancelar
          </Button>
          <ActionButton
            onClick={handleCrearPedido}
            disabled={creandoPedido || clienteSeleccionado === null || direccionSeleccionada === null || productosCarrito.length === 0}
            startIcon={creandoPedido ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Add />}
            sx={{ width: { xs: '100%', sm: 'auto' }, fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            {creandoPedido ? 'Creando Pedido...' : 'Crear Pedido'}
          </ActionButton>
        </DialogActions>
      </Dialog>
    </DashboardContainer>
  );
};

export default DashboardPedidosOnline;